import { useCallback, useEffect, useRef, useState } from 'react'
import { quoteDelivery } from '../services/orderService'

/**
 * The delivery fee, kept true for as long as the summary is on screen.
 *
 * The server picks a rider, prices their trip to the shop into the total, and holds them
 * for thirty seconds. After that the hold lapses and the figure could be somebody else's,
 * so this asks again — **silently**.
 *
 * Silent is the whole design, and it is three rules:
 *
 *   1. **The button is only disabled on the first load.** A re-quote that greys out
 *      *Iorder na* every thirty seconds makes the screen flicker under somebody who reads
 *      slowly, which is the exact clutter this redesign removes.
 *   2. **No countdown.** A ticking clock on a payment screen hurries the one person we
 *      deliberately gave time to. One quiet line does the same job honestly.
 *   3. **Nothing is announced when nothing changed** — and usually nothing has, because
 *      the rider who won a minute ago normally wins again.
 *
 * Every call costs a real rider thirty seconds out of somebody else's pool, so it fires
 * once per route and once per interval, and never on a render.
 */

/** Matches `Quote.LIFETIME_MS` on the server. Re-asked just before the hold lapses. */
const REFRESH_MS = 30000

/**
 * One id for as long as this summary is on screen.
 *
 * The server holds a rider under it, and the thirty-second re-quote sends the same one so
 * it can keep the rider it already holds. Without it every re-ask is a stranger to its own
 * hold: gate 10 skips the held rider, and a customer with one rider online is told "no
 * rider available" by the hold they themselves created.
 *
 * Minted once per mount rather than per request — a fresh id every time would recreate exactly
 * the bug it exists to prevent.
 */
const newSessionId = () =>
  crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`

const routeOf = (partnerSlug, pickup, dropoff) => {
  if (pickup?.lat == null || dropoff?.lat == null || !partnerSlug) return null

  return {
    partnerSlug,
    pickupLatitude: pickup.lat,
    pickupLongitude: pickup.lng,
    dropoffLatitude: dropoff.lat,
    dropoffLongitude: dropoff.lng,
  }
}

export function useDeliveryQuote({ partnerSlug, pickup, dropoff, active }) {
  const [state, setState] = useState({
    loading: false,
    quote: null,
    unavailable: null,
    error: null,
  })

  const session = useRef(newSessionId())

  const route = routeOf(partnerSlug, pickup, dropoff)
  const key = route ? JSON.stringify(route) : null

  /**
   * The figure currently on screen, so a refresh can tell whether anything changed.
   *
   * A ref rather than state: reading it must not make the effect below depend on the
   * value it is about to write, which is how a re-quote turns into a loop.
   */
  const shown = useRef(null)
  const [changed, setChanged] = useState(null)

  const ask = useCallback(async (first, signal) => {
    if (!key) return

    if (first) setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      const result = await quoteDelivery({ ...JSON.parse(key), sessionId: session.current }, { signal })
      const data = result.data

      if (!data.available) {
        shown.current = null
        setState({ loading: false, quote: null, unavailable: data, error: null })
        return
      }

      // Only worth saying when it is actually different. Most refreshes land on the same
      // rider and the same number, and the customer should see nothing at all.
      if (shown.current != null && shown.current !== data.deliveryFee) {
        setChanged({ from: shown.current, to: data.deliveryFee })
      }

      shown.current = data.deliveryFee
      setState({ loading: false, quote: data, unavailable: null, error: null })
    } catch (error) {
      if (error.name === 'AbortError') return

      /**
       * A failed **refresh** keeps the figure that is already on screen.
       *
       * It was true thirty seconds ago and the server will settle the difference at
       * placement anyway — where an expired quote is a one-tap confirmation. Blanking a
       * good number because the network hiccuped would strand a customer who is finished.
       */
      setState((prev) => (first
        ? { loading: false, quote: null, unavailable: null, error }
        : prev))
    }
  }, [key])

  useEffect(() => {
    if (!active || !key) return undefined

    const controller = new AbortController()

    ask(true, controller.signal)

    const timer = setInterval(() => ask(false, controller.signal), REFRESH_MS)

    return () => {
      controller.abort()
      clearInterval(timer)
    }
  }, [active, key, ask])

  return {
    ...state,
    /** Set once when the amount moves, and cleared by the screen that showed it. */
    changed,
    acknowledgeChange: () => setChanged(null),
    /** What `X-Quote-Id` carries. Null until the first quote lands. */
    token: state.quote?.quoteToken ?? null,

    /**
     * Silent. For the 409 at placement, where a good figure is already on screen and
     * blanking it would take the customer backwards.
     */
    refresh: () => ask(false),

    /**
     * Visible. For the customer pressing "subukan ulit" after the first quote failed —
     * there is nothing on screen to protect, and a retry that shows no sign of running
     * invites them to press it again.
     */
    retry: () => ask(true),
  }
}
